/**
 * React Query hook for Practice Areas (Global)
 * Provides automatic caching, background sync, and error handling
 * Can be used across all modules
 */

import { useQuery } from '@tanstack/react-query';
import { getPracticeAreas } from 'services/CaseServices';
import { totalCountEvent } from 'services/generalServices';
import { queryKeys } from '../config/queryClient';

/**
 * Fetches practice areas data
 *
 * @param {Object} options - Query options
 * @param {boolean} options.loadFullList - If true, fetches all practice areas, otherwise uses pagination (default: true)
 * @param {number} options.pageSize - Number of items per page (default: 10)
 * @param {string} options.status - Filter by status (default: 'active')
 * @param {boolean} options.excludeFromLoading - Exclude from global loading state (default: false)
 * @param {string} options.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {string} options.pagination_token - Pagination token (default: '')
 * @param {string} options.pagination_direction - Pagination direction (default: 'forward')
 * @returns {Promise<Array>} - Array of practice areas
 */
const fetchPracticeAreas = async ({
  loadFullList = true,
  pageSize = 10,
  status = 'active',
  excludeFromLoading = false,
  sort_order = 'alphabet_ascending',
  pagination_token = '',
  pagination_direction = 'forward',
}) => {
  try {
    let pageLimit = pageSize;

    if (loadFullList) {
      // Get total count first
      const countResponse = await totalCountEvent('practice-area', '', '', '', '', excludeFromLoading);
      pageLimit = countResponse?.data || pageSize;
    }

    // Fetch practice areas using CaseServices (which already supports sort_order)
    const response = await getPracticeAreas(
      null, // tenantId - will be retrieved internally by getTenantDetails()
      null, // tenantSk - will be retrieved internally by getTenantDetails()
      pageLimit,
      pagination_token,
      pagination_direction,
      { status }, // filters object
      excludeFromLoading,
      sort_order
    );

    return response?.data?.['practice-area'] || [];
  } catch (error) {
    console.error('Error fetching practice areas:', error);
    throw error;
  }
};

/**
 * Hook to fetch practice areas with React Query
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.loadFullList - If true, loads all practice areas (default: true)
 * @param {number} options.pageSize - Number of items per page (default: 10)
 * @param {string} options.status - Filter by status (default: 'active')
 * @param {boolean} options.excludeFromLoading - Exclude from global loading state (default: false)
 * @param {string} options.sort_order - Sort order (default: 'alphabet_ascending')
 * @param {string} options.pagination_token - Pagination token (default: '')
 * @param {string} options.pagination_direction - Pagination direction (default: 'forward')
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} - practiceAreaList, isPracticeAreaLoading, error, refetch, isFetching
 */
export const usePracticeAreasQuery = ({
  loadFullList = true,
  pageSize = 10,
  status = 'active',
  excludeFromLoading = false,
  sort_order = 'alphabet_ascending',
  pagination_token = '',
  pagination_direction = 'forward',
  enabled = true,
} = {}) => {
  // Build query key with all parameters for proper caching
  const queryKey = queryKeys.practiceAreas.list({
    status,
    sort_order,
    pagination_token,
    pagination_direction,
    pageSize,
    loadFullList,
  });

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchPracticeAreas({
        loadFullList,
        pageSize,
        status,
        excludeFromLoading,
        sort_order,
        pagination_token,
        pagination_direction,
      }),
    enabled,
    staleTime: 10 * 60 * 1000, // Consider data stale after 10 minutes
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes (gcTime replaces cacheTime in v4+)
    refetchOnWindowFocus: false, // Don't refetch on window focus for relatively static data
    retry: 2, // Retry twice on failure
  });

  return {
    practiceAreaList: data || [],
    isPracticeAreaLoading: isLoading,
    isFetching,
    error,
    refetch,
  };
};
